import { type ReactNode } from 'react';
import { type ToastVariant } from './Toast';

// ─── Types ─────────────────────────────────────────────────────────────────

type ToastOptions = {
  /** Main content: text, Trans components, or JSX */
  message: ReactNode;
  /** Secondary line under the message */
  description?: ReactNode;
  /**
   * Stable identity. Firing again with the same id replaces the open toast and
   * restarts its timer instead of stacking a second one, and is the handle for
   * `toast.dismiss(id)`. Defaults to a generated id.
   */
  id?: string;
  /** Seconds before auto-dismiss. `0` keeps the toast open until it is dismissed */
  duration?: number;
  /** Shows a bar depleting over `duration`. Pauses while the viewport is hovered */
  showProgress?: boolean;
  /** Right-side action slot (buttons, links) */
  action?: ReactNode;
  /** Called once the toast is gone — auto-dismiss, close button, swipe, or `dismiss()` */
  onClose?: () => void;
  /** Overrides the default 384px width */
  width?: number;
  /** Additional CSS class on the toast element */
  className?: string;
  /** Test identifier. Defaults to `toast`; the close button derives `{dataTestId}-close` */
  dataTestId?: string;
};

type ToastEntry = Omit<ToastOptions, 'id' | 'dataTestId'> & {
  id: string;
  dataTestId: string;
  variant: ToastVariant;
  /** Bumped every time the same id is re-fired, so the Toaster remounts it and the timer restarts */
  version: number;
};

export type { ToastEntry, ToastOptions };

// ─── Store ─────────────────────────────────────────────────────────────────

// A module-level store rather than React state: `toast.error(…)` has to be
// callable from sagas, react-query callbacks and other non-React code, exactly
// like the antd singleton it replaces. The Toaster subscribes with
// useSyncExternalStore, so `entries` must be replaced, never mutated.

let entries: ToastEntry[] = [];
let lastGeneratedId = 0;
const listeners = new Set<() => void>();

function setEntries(next: ToastEntry[]) {
  entries = next;
  listeners.forEach((listener) => listener());
}

function subscribeToToasts(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getToasts(): ToastEntry[] {
  return entries;
}

function openToast(variant: ToastVariant, options: ToastOptions): string {
  const { id: requestedId, dataTestId = 'toast', ...rest } = options;

  lastGeneratedId += 1;
  const id = requestedId ?? `toast-${lastGeneratedId}`;
  const previous = entries.find((entry) => entry.id === id);
  const entry: ToastEntry = {
    ...rest,
    id,
    dataTestId,
    variant,
    version: (previous?.version ?? 0) + 1,
  };

  setEntries(
    previous ? entries.map((current) => (current.id === id ? entry : current)) : [...entries, entry],
  );

  return id;
}

/** Removes a toast and fires its `onClose`. Used by the Toaster and by `toast.dismiss`. */
function closeToast(id: string) {
  const entry = entries.find((current) => current.id === id);
  if (!entry) {
    return;
  }

  setEntries(entries.filter((current) => current.id !== id));
  entry.onClose?.();
}

function dismissToasts(id?: string) {
  if (id !== undefined) {
    closeToast(id);
    return;
  }

  const closing = entries;
  setEntries([]);
  closing.forEach((entry) => entry.onClose?.());
}

export { closeToast, getToasts, subscribeToToasts };

// ─── Public API ────────────────────────────────────────────────────────────

/**
 * Imperative toast API. Import it anywhere — no hook, no context — and render a
 * single `<Toaster />` at the app root for the toasts to appear in.
 *
 * ```ts
 * toast.error({ message: t('error.mutation.archiveDocument') });
 * const id = toast.info({ message: 'Sending…', duration: 5, showProgress: true });
 * toast.dismiss(id);
 * ```
 */
const toast = {
  /** Blue. Returns the toast id. */
  info: (options: ToastOptions) => openToast('info', options),
  /** Green. Returns the toast id. */
  success: (options: ToastOptions) => openToast('success', options),
  /** Yellow. Returns the toast id. */
  warning: (options: ToastOptions) => openToast('warning', options),
  /** Red — renders the `danger` variant. Returns the toast id. */
  error: (options: ToastOptions) => openToast('danger', options),
  /** Closes one toast, or every open toast when called with no id. Fires their `onClose`. */
  dismiss: dismissToasts,
};

export { toast };
